import { SearchOutlined } from "@ant-design/icons"
import { type InputRef, Popover, Spin, Tag, Typography } from "antd"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { TextInput } from "../../components/Input/TextInput.tsx"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import {
  GLOBAL_SEARCH_MIN_LENGTH,
  type GlobalSearchGroup,
  type GlobalSearchResult,
  searchEverywhere,
  searchableGroups
} from "./global-search.ts"

const { Text } = Typography

// Long enough that steady typing sends one search, not one per letter.
const DEBOUNCE_MS = 350
const LISTBOX_ID = "global-search-results"

const isMac =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform)

/**
 * One search across leads, projects, customers and the archive, in the header,
 * so a name or a phone number finds the record without guessing which list it
 * is in. `className` only sizes the field; the shell bounds it.
 */
export function GlobalSearch({ className }: { className?: string } = {}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const permissions = useAppSelector((state) => state.auth.permissions)
  const inputRef = useRef<InputRef>(null)
  const latest = useRef(0)

  const [term, setTerm] = useState("")
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [groups, setGroups] = useState<GlobalSearchGroup[]>([])
  const [groupsTerm, setGroupsTerm] = useState("")
  // A key, not an index: groups arrive in any order and must not move it.
  const [activeKey, setActiveKey] = useState<string | null>(null)

  const searchable = searchableGroups(permissions)
  const canSearch = searchable.length > 0
  const q = term.trim()
  const ready = q.length >= GLOBAL_SEARCH_MIN_LENGTH
  // Rows from an earlier term stay visible while the new one loads, but Enter
  // must not open one of them.
  const fresh = groupsTerm === q

  useEffect(() => {
    if (!canSearch) return
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.isComposing) return
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault()
        inputRef.current?.focus()
        setOpen(true)
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [canSearch])

  useEffect(() => {
    if (!ready) {
      latest.current += 1
      setGroups([])
      setLoading(false)
      return
    }
    const request = ++latest.current
    const controller = new AbortController()
    const order = searchableGroups(permissions)
    let first = true
    setLoading(true)
    const timer = window.setTimeout(async () => {
      try {
        await searchEverywhere(q, permissions, {
          signal: controller.signal,
          onGroup: (group) => {
            // An older, slower search must not paint over the newer term's rows.
            if (request !== latest.current) return
            // This search's first group replaces the earlier term's rows; the
            // rest join it in their usual order as they answer.
            const replace = first
            first = false
            setGroupsTerm(q)
            setGroups((shown) =>
              [
                ...(replace ? [] : shown.filter((g) => g.key !== group.key)),
                group
              ].sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key))
            )
            // The highlight lands on the first row shown and stays on it, so a
            // group answering later above it cannot move it under Enter.
            const top = group.results[0]?.key ?? null
            setActiveKey((key) => (replace ? top : (key ?? top)))
          }
        })
      } finally {
        if (request === latest.current) setLoading(false)
      }
    }, DEBOUNCE_MS)
    return () => {
      window.clearTimeout(timer)
      // Typing on or closing stops the old search's requests, so they do not
      // hold up the server ahead of the one the user is waiting for.
      controller.abort()
    }
  }, [q, ready, permissions])

  const flat = useMemo(() => groups.flatMap((group) => group.results), [groups])
  const active = Math.max(
    0,
    flat.findIndex((result) => result.key === activeKey)
  )

  useEffect(() => {
    document
      .getElementById(`global-search-option-${active}`)
      ?.scrollIntoView?.({ block: "nearest" })
  }, [active])

  const close = () => {
    setOpen(false)
    setTerm("")
    setGroups([])
    setGroupsTerm("")
    setActiveKey(null)
  }

  const openResult = (result: GlobalSearchResult) => {
    close()
    inputRef.current?.blur()
    navigate(result.href)
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      close()
      inputRef.current?.blur()
    } else if (event.key === "ArrowDown" && flat.length) {
      event.preventDefault()
      setActiveKey(flat[(active + 1) % flat.length].key)
    } else if (event.key === "ArrowUp" && flat.length) {
      event.preventDefault()
      setActiveKey(flat[(active - 1 + flat.length) % flat.length].key)
    } else if (event.key === "Enter" && fresh && flat[active]) {
      event.preventDefault()
      openResult(flat[active])
    }
  }

  if (!canSearch) return null

  const noMatches =
    !loading &&
    ready &&
    fresh &&
    groups.length > 0 &&
    flat.length === 0 &&
    groups.every((group) => !group.failed)

  let index = -1
  const content = (
    <div
      className={`max-h-[60vh] w-[420px] max-w-[90vw] overflow-y-auto ${
        fresh ? "" : "opacity-60"
      }`}
      data-testid={"global-search-results"}
    >
      {loading && !flat.length ? (
        <div className={"flex justify-center py-6"}>
          <Spin size={"small"} />
        </div>
      ) : noMatches ? (
        <Text type={"secondary"} className={"block py-4 text-center"}>
          {t("globalSearch.empty", { term: q })}
        </Text>
      ) : (
        <div id={LISTBOX_ID} role={"listbox"} className={"flex flex-col gap-3"}>
          {groups
            .filter((group) => group.results.length || group.failed)
            .map((group) => (
              <div
                key={group.key}
                role={"group"}
                aria-label={t(`globalSearch.groups.${group.key}`)}
              >
                <Text
                  type={"secondary"}
                  className={"block px-2 pb-1 text-xs uppercase"}
                >
                  {t(`globalSearch.groups.${group.key}`)}
                </Text>
                {group.failed && (
                  <Text type={"secondary"} className={"block px-2 text-xs"}>
                    {t("globalSearch.failed")}
                  </Text>
                )}
                {group.results.map((result) => {
                  index += 1
                  const selected = index === active
                  const position = index
                  return (
                    <div
                      key={result.key}
                      id={`global-search-option-${position}`}
                      role={"option"}
                      aria-selected={selected}
                      className={`flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 ${
                        selected ? "bg-[var(--ant-control-item-bg-hover)]" : ""
                      }`}
                      onMouseEnter={() => setActiveKey(result.key)}
                      onMouseDown={(event) => {
                        // Keep focus in the input until the click lands.
                        event.preventDefault()
                        openResult(result)
                      }}
                    >
                      <div className={"min-w-0 flex-1"}>
                        <div className={"truncate font-medium"}>
                          {result.title}
                        </div>
                        {result.subtitle && (
                          <Text
                            type={"secondary"}
                            className={"block truncate text-xs"}
                          >
                            {result.subtitle}
                          </Text>
                        )}
                      </div>
                      {result.archived && (
                        <Tag className={"m-0 shrink-0"}>
                          {t(`globalSearch.archived.${result.archived}`)}
                        </Tag>
                      )}
                    </div>
                  )
                })}
              </div>
            ))}
          {loading && fresh && groups.length < searchable.length && (
            <Text
              type={"secondary"}
              className={"flex items-center gap-2 px-2 text-xs"}
              data-testid={"global-search-still-searching"}
            >
              <Spin size={"small"} />
              {t("globalSearch.searching")}
            </Text>
          )}
        </div>
      )}
    </div>
  )

  return (
    <Popover
      open={open && ready}
      content={content}
      placement={"bottomRight"}
      arrow={false}
      destroyTooltipOnHide
    >
      <TextInput
        ref={inputRef}
        disableAutoCapitalize
        value={term}
        allowClear
        className={className ?? "w-72"}
        role={"combobox"}
        aria-label={t("globalSearch.label")}
        aria-expanded={open && ready}
        aria-controls={LISTBOX_ID}
        aria-activedescendant={
          flat.length ? `global-search-option-${active}` : undefined
        }
        prefix={<SearchOutlined className={"text-gray-400"} />}
        suffix={
          // slate-600, not AntD's secondary token: that is rgba(0,0,0,.45),
          // which is 4.0:1 on white and fails AA at this size.
          <Text className={"text-xs text-slate-600"}>
            {isMac ? "⌘K" : "Ctrl K"}
          </Text>
        }
        placeholder={t("globalSearch.placeholder")}
        onChange={(event) => {
          setTerm(event.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
      />
    </Popover>
  )
}
